/**
 *      ▄ █
 *    █ █ █▄▄  LOADBALANCER CLIENT
 *    █ █ ▄ █
 *    ▀     ▀  @copyright Copyright (c) 2025
 */
import { BaseComponent } from '../../../../../../core/components';
import { BillingApiService } from '../../../http';
import { ChangeDetectorRef, Component, Input, OnInit, QueryList, ViewChildren } from '@angular/core';
import { CheckoutService } from '../../../services/component';
import { ErrorResponseModel } from '../../../../../../core/models';
import { LogService, NotifyService, StripeService } from '../../../../../../core/services';
import { PortalRbacService } from '../../../services/utils';
import { Router } from '@angular/router';
import { forkJoin, Observable, Subscriber } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-checkout-helper',
    templateUrl: './checkout-helper.component.html',
    styleUrls: ['./checkout-helper.component.scss'],
    providers: [
        StripeService
    ]
})
export class CheckoutHelperComponent extends BaseComponent implements OnInit {
    /**
     * Get all the steps from the view, so we can scroll to the active step.
     */
    @ViewChildren('step') public steps: QueryList<any> = new QueryList<any>();

    /**
     * Incoming product key.
     */
    @Input() public productId: string = null;

    /**
     * Show existing cart items.
     */
    @Input() public checkout: boolean = false;

    /**
     * Set if adding a new card only.
     */
    @Input() public newCard: boolean = false;

    /**
     * Active step.
     */
    public activeStep: number = 0;

    /**
     * Store is the next button is valid
     */
    public nextButtonValid: boolean = false;

    /**
     * Hide set up steps
     */
    public hideSetUpSteps: boolean = false;

    /**
     * Hide purchase steps
     */
    public hidePurchaseSteps: boolean = false;

    /**
     * Set is loading state.
     */
    public isLoading: boolean = true;

    /**
     * Number of visible steps.
     */
    public numberVisibleSteps: number = 0;

    /**
     * Set which is the first step (its conditionally set in setView)
     */
    public firstVisibleStep: number = 1;

    constructor(
        private cdr: ChangeDetectorRef,
        private billingApiService: BillingApiService,
        private checkoutService: CheckoutService,
        private logService: LogService,
        private notifyService: NotifyService,
        private portalRbacService: PortalRbacService,
        private router: Router,
        private stripeService: StripeService,
    ) {
        super();
    }

    ngOnInit(): void {
        this.setUpPage();
        this.subscribeToCheckoutService();
        this.cdr.detectChanges(); // Added as unit tests fail due to changes on activeStep and nextButtonValid

        if (this.newCard) {
            this.hidePurchaseSteps = true;
        }
    }

    /**
     * Help function to take the user back to the page they came from.
     */
    public goBackToBillingOverview(): void {
        const tabFilter: string = this.newCard && !this.productId ? 'methods' : 'items';
        this.router.navigateByUrl(
            '/cm/organisation/billing/overview',
            { state: { tabFilter: tabFilter } }
        ).then().catch();
    }

    /**
     * Trigger back a step.
     */
    public back(): void {
        this.checkoutService.backStep();
        this.scrollToActiveStep();
    }

    /**
     * Trigger next step.
     */
    public next(): void {
        if (this.activeStep === 2) {
            this.checkoutService.childSubmitEmitter.next(this.activeStep);
        } else {
            this.checkoutService.nextStep();
        }
        this.checkoutService.isStepValidEmitter.next(false);
        this.scrollToActiveStep();
    }

    /**
     * Trigger submit card button.
     */
    public triggerSubmitCardButton(): void {
        this.checkoutService.childSubmitEmitter.next(this.activeStep);
    }

    /**
     * Purchase subscription.
     */
    public purchaseSubscription(): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            const cartItems = this.checkoutService.cartItems;
            const selectedTier = this.getSelectedTier(cartItems);
            const subscription = {
                'payment_method_id': this.checkoutService.selectedCardId,
                prices: this.formatPrices(cartItems)
            };

            this.billingApiService.createSubscription(subscription)
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (): void => {
                        this.portalRbacService.selectedTier = selectedTier;
                        this.checkoutService.emptyCartItems();
                        this.goBackToBillingOverview();
                        observer.next();
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.logService.error('CheckoutComponent:purchaseSubscription', error.message);
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Get selected tier.
     * @param cartItems
     * @private
     */
    private getSelectedTier(cartItems: any[]): string {
        const portalSubscription = cartItems.find(item => item.product.product_type === 'portal');

        return this.matchProductToTier(portalSubscription);
    }

    /**
     * Match product to tier.
     * @param portalSubscription
     * @private
     */
    private matchProductToTier(portalSubscription: { product: { id: any; }; }) {
        switch (portalSubscription?.product?.id) {
            case 'prod_RqneLck4KA46qU': // ADC Portal Trial
            case 'prod_RqnczfveFd3kgE': // ADC Portal Business
                return 'business';
            case 'prod_RqnaPfUxle9uh3': // ADC Portal Pro
                return 'professional';
            case 'prod_RqnZsTYmuFHTXr':
                return 'free';
            default:
                this.logService.error(
                    'CheckoutHelperComponent:matchProductToTier',
                    'Unable to match product to tier, seeting to \'free\' tier by default');

                return 'free';
        }
    }

    /**
     * Format prices.
     * @param cartItems
     * @private
     */
    private formatPrices(cartItems: any[]): any[] {
        return cartItems.map((item: any): any => {
            return {
                id: item.price.id,
                quantity: item.quantity
            };
        });
    }

    /**
     * Set up page
     * @private
     */
    private setUpPage(): void {
        this.notifyService.muteLocalNotifications();
        const apiCalls: any = {
            key: this.billingApiService.getStripeKey(),
            account: this.billingApiService.getAccount(),
            cards: this.billingApiService.getPaymentMethods()
        };

        forkJoin(apiCalls)
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (data: any): void => {
                    if (!data.cards?.length || this.newCard) {
                        this.setUpCardElement(data);
                    } else {
                        this.setUpView(data);
                    }
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error('CheckoutComponent:setUpPage', error.message);
                }
            });
    }

    /**
     * Set up card element in stripe.
     * @param data
     * @private
     */
    private setUpCardElement(data: any): void {
        this.notifyService.muteLocalNotifications();
        this.billingApiService.getClientSecretKey()
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (clientSecret: any): void => {
                    this.stripeService.initialiseStripe(data.key?.stripePubKey);
                    this.stripeService.setUpElementOptions(clientSecret);
                    this.setUpView(data);
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error('CheckoutComponent:setUpCardElement', error.message);
                    this.isLoading = false;
                    this.notifyService.unmuteLocalNotifications();
                }
            });
    }

    /**
     * Set up view - move user to the correct step based on data.
     * @param data
     * @private
     */
    private setUpView(data: any): void {
        const hasAccountDetails: boolean = !!Object.keys(data['account']).length;
        if (this.checkout && this.checkoutService.cartItems.length) {
            this.hideSetUpSteps = true;
            this.checkoutService.setStep(3);
            this.firstVisibleStep = 3;
        } else if (hasAccountDetails && data['cards'].length && this.productId) {
            this.hideSetUpSteps = true;
            this.checkoutService.setStep(3);
            this.firstVisibleStep = 3;
        } else if (!this.productId && hasAccountDetails) {
            this.checkoutService.setStep(2);
            this.firstVisibleStep = 2;
        } else {
            this.checkoutService.setStep(1);
        }

        this.notifyService.unmuteLocalNotifications();
        this.isLoading = false;

        const elements: any[] = this.steps.toArray();
        this.numberVisibleSteps = elements.filter((item: any): boolean => !item.nativeElement.hidden).length;
    }

    /**
     * Subscribe to check out service.
     * @private
     */
    private subscribeToCheckoutService(): void {
        this.checkoutService.activeStepEmitter
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (stepIndex: any): void => {
                    this.activeStep = stepIndex;
                    const activeStepValid = this.isActiveStepValid();
                    this.checkoutService.isStepValidEmitter.next(activeStepValid);
                }
            });

        this.checkoutService.isStepValidEmitter
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (state: boolean): void => {
                    if (!this.isLoading) {
                        this.nextButtonValid = state;
                    }
                }
            });
    }

    /**
     * Check if active step is valid.
     * @private
     */
    private isActiveStepValid(): boolean {
        if (this.activeStep === 4) {
            return this.checkoutService.selectedCardId !== null;
        }
    }

    /**
     * Scroll to active step.
     * @private
     */
    private scrollToActiveStep(): void {
        const elements: any[] = this.steps.toArray();
        elements[this.activeStep - 1].nativeElement.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
            inline: 'nearest'
        });
    }
}
